import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Copy, Link as LinkIcon, X, Clock } from "lucide-react";

export default function ShareModal({ assessmentId, open, onClose }) {
  const [creating, setCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);

  const create = async () => {
    try {
      setCreating(true);
      const { data } = await api.post(`/assessment/${assessmentId}/share`);
      const url = `${window.location.origin}/shared/${data.token}`;
      setShareUrl(url);
      setExpiresAt(data.expires_at);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not create share link.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };

  if (!open) return null;

  const expiryLabel = expiresAt
    ? new Date(expiresAt).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Share assessment"
        className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <div className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-teal-600" />
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Shareable link</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-6 pt-2 pb-6">
          <h3 className="font-display text-xl font-semibold text-slate-900 tracking-tight">
            Share a read-only link
          </h3>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
            Anyone with this link can view your assessment summary. The link is
            <span className="font-medium text-slate-900"> read-only </span>
            and <span className="font-medium text-slate-900">expires after 30 days</span>.
          </p>

          {!shareUrl ? (
            <button
              onClick={create}
              disabled={creating}
              data-testid="create-share-btn"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {creating ? "Creating link…" : "Generate share link (30 days)"}
            </button>
          ) : (
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  data-testid="share-url-input"
                  className="flex-1 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={copy}
                  data-testid="copy-share-url"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors"
                >
                  <Copy className="h-4 w-4" /> Copy
                </button>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                <Clock className="h-3.5 w-3.5" /> Expires {expiryLabel}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
